import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowUpRight, Navigation } from "lucide-react";
import { openWhatsApp, PHONE_DISPLAY, EMAIL, ADDRESS } from "../lib/whatsapp";
import { useT } from "../lib/i18n";

export default function Contact() {
    const t = useT();

    const channels = [
        {
            icon: MessageCircle,
            label: t("WhatsApp", "వాట్సాప్"),
            value: PHONE_DISPLAY,
            sub: t("Fastest response · 9 AM – 9 PM", "వేగవంతమైన ప్రతిస్పందన · ఉ. 9 – రా. 9"),
            action: () => openWhatsApp(t("Hello RVS Electronics, I'd like to get in touch.", "హలో RVS ఎలక్ట్రానిక్స్, మీతో సంప్రదించాలనుకుంటున్నాను.")),
            cta: t("Open WhatsApp", "వాట్సాప్ తెరవండి"),
            accent: "#25D366",
            testId: "contact-whatsapp-card",
        },
        {
            icon: Phone,
            label: t("Phone", "ఫోన్"),
            value: PHONE_DISPLAY,
            sub: t("Telugu · Hindi · English", "తెలుగు · హిందీ · ఇంగ్లీష్"),
            href: "tel:+918341717181",
            cta: t("Call Now", "ఇప్పుడే కాల్ చేయండి"),
            accent: "#FF5A00",
            testId: "contact-phone-card",
        },
        {
            icon: Mail,
            label: t("Email", "ఇమెయిల్"),
            value: EMAIL,
            sub: t("For partnership & retailer queries", "భాగస్వామ్యం & రిటైలర్ ప్రశ్నల కోసం"),
            href: `mailto:${EMAIL}`,
            cta: t("Send Email", "ఇమెయిల్ పంపండి"),
            accent: "#0B192C",
            testId: "contact-email-card",
        },
    ];

    return (
        <div data-testid="page-contact">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 md:pb-16">
                <div className="max-w-3xl">
                    <p className="eyebrow">{t("Contact", "సంప్రదించండి")}</p>
                    <h1 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-rvs-navy">
                        {t("Talk to a real", "నిజమైన మనిషితో")}<br />
                        <span className="text-rvs-orange">{t("human in Armoor.", "ఆర్మూర్‌లో మాట్లాడండి.")}</span>
                    </h1>
                    <p className="mt-7 text-lg md:text-xl text-[#4A5568] leading-relaxed">
                        {t("WhatsApp is the fastest way to reach us. Drop a message — a member of our team will respond within minutes during business hours.", "మాకు చేరుకోవడానికి వాట్సాప్ అత్యంత వేగమైన మార్గం. సందేశం పంపండి — వ్యాపార సమయాల్లో మా టీమ్ సభ్యుడు నిమిషాల్లోనే ప్రతిస్పందిస్తారు.")}
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {channels.map((c, i) => {
                        const Icon = c.icon;
                        const Wrapper = c.href ? "a" : "button";
                        return (
                            <Wrapper
                                key={i}
                                {...(c.href ? { href: c.href } : { onClick: c.action })}
                                data-testid={c.testId}
                                className="card-default p-7 text-left flex flex-col fade-up"
                                style={{animationDelay:`${i*80}ms`}}
                            >
                                <span className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${c.accent}1f`, color: c.accent }}>
                                    <Icon className="w-6 h-6" />
                                </span>
                                <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-rvs-orange">{c.label}</p>
                                <p style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-2 text-xl font-bold text-rvs-navy break-all">{c.value}</p>
                                <p className="mt-2 text-sm text-[#4A5568]">{c.sub}</p>
                                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-rvs-navy">
                                    {c.cta} <ArrowUpRight className="w-4 h-4" />
                                </span>
                            </Wrapper>
                        );
                    })}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
                <div className="grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-7 rounded-[20px] overflow-hidden border border-rvs-navy-10 bg-white">
                        <iframe
                            title="RVS Electronics Location"
                            src="https://www.google.com/maps?q=RTC+Bus+Stand+Armoor+Nizamabad+Telangana+503224&hl=en&z=15&output=embed"
                            width="100%"
                            height="500"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map-iframe"
                        ></iframe>
                    </div>
                    <div className="md:col-span-5">
                        <div className="bg-[#0B192C] text-white rounded-[20px] p-8 md:p-10 h-full grid-bg relative overflow-hidden">
                            <div className="absolute -bottom-32 -left-32 w-[280px] h-[280px] rounded-full bg-[#FF5A00]/30 blur-[110px]" aria-hidden></div>
                            <div className="relative space-y-7">
                                <div>
                                    <p className="eyebrow text-[#FF5A00]">{t("Visit our shop", "మా షాపును సందర్శించండి")}</p>
                                    <h2 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-3 text-3xl font-black tracking-tight">RVS Electronics</h2>
                                </div>
                                <div className="flex items-start gap-3 text-white/85">
                                    <MapPin className="w-5 h-5 text-[#FF5A00] mt-0.5 shrink-0" />
                                    <p className="text-sm leading-relaxed">{ADDRESS}</p>
                                </div>
                                <div className="flex items-start gap-3 text-white/85">
                                    <Clock className="w-5 h-5 text-[#FF5A00] mt-0.5 shrink-0" />
                                    <div className="text-sm leading-relaxed">
                                        <p className="font-semibold text-white">{t("Business Hours", "వ్యాపార సమయాలు")}</p>
                                        <p>{t("Monday – Saturday · 9:00 AM – 9:00 PM", "సోమవారం – శనివారం · ఉ. 9 – రా. 9")}</p>
                                        <p>{t("Sunday · 10:00 AM – 6:00 PM", "ఆదివారం · ఉ. 10 – సా. 6")}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 text-white/85">
                                    <Phone className="w-5 h-5 text-[#FF5A00] mt-0.5 shrink-0" />
                                    <a href="tel:+918341717181" data-testid="contact-side-phone" className="text-sm hover:text-white">{PHONE_DISPLAY}</a>
                                </div>
                                <div className="flex items-start gap-3 text-white/85">
                                    <Mail className="w-5 h-5 text-[#FF5A00] mt-0.5 shrink-0" />
                                    <a href={`mailto:${EMAIL}`} data-testid="contact-side-email" className="text-sm hover:text-white break-all">{EMAIL}</a>
                                </div>
                                <a
                                    href="https://www.google.com/maps/dir/?api=1&destination=RTC+Bus+Stand+Armoor+Nizamabad+Telangana+503224"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-directions-btn"
                                    className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                                >
                                    <Navigation className="w-4 h-4" /> {t("Get Directions", "దారి పొందండి")}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
